import { Holiday, yearScene } from './year-scene';

/**
 * The day-cycle themes follow the sun as it is in Denmark: up 04:00–22:00 at midsummer and
 * 08:00–18:00 at midwinter, and in between through the year along a smooth curve. Morning, midday
 * and evening are the thirds of the daylight; the rest is night.
 */
export type DayPhase = 'morning' | 'noon' | 'evening' | 'night';

/** Minutes after midnight. */
const SUMMER_RISE = 4 * 60;
const SUMMER_SET = 22 * 60;
const WINTER_RISE = 8 * 60;
const WINTER_SET = 18 * 60;
const DAY = 24 * 60;

/** The day of the year of the summer solstice (21 June). */
const MIDSUMMER = 172;

/** How far towards midwinter `date` is: 0 at midsummer, 1 at midwinter, along a cosine. */
function winterness(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 1);
  const dayOfYear =
    Math.round(
      (new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() -
        start.getTime()) /
        86_400_000,
    ) + 1;
  return (1 - Math.cos((2 * Math.PI * (dayOfYear - MIDSUMMER)) / 365)) / 2;
}

/** Sunrise and sunset on `date`, in minutes after midnight on the screen's own clock. */
export function sunTimes(date: Date): { rise: number; set: number } {
  const w = winterness(date);
  return {
    rise: Math.round(SUMMER_RISE + (WINTER_RISE - SUMMER_RISE) * w),
    set: Math.round(SUMMER_SET + (WINTER_SET - SUMMER_SET) * w),
  };
}

const minutesOf = (date: Date) => date.getHours() * 60 + date.getMinutes();

/** Which part of the day `now` is in: the thirds of the daylight, or night. */
export function dayPhase(now: Date): DayPhase {
  const { rise, set } = sunTimes(now);
  const minutes = minutesOf(now);
  if (minutes < rise || minutes >= set) return 'night';
  const share = (minutes - rise) / (set - rise);
  return share < 1 / 3 ? 'morning' : share < 2 / 3 ? 'noon' : 'evening';
}

/**
 * The host class for a theme: `theme-classic`, `theme-halloween` …, and for the day-cycle themes
 * the part of the day — Classic in its own four versions, and Light-and-dark light through the
 * day and dark at night, e.g. `theme-cycle-light-noon` or `theme-cycle-dark-night`.
 */
export function themeClass(theme: string, now: Date, hidden: readonly Holiday[] = []): string {
  if (theme === 'DayCycleClassic')
    return `theme-cycle-classic-${dayPhase(now)}`;
  if (theme === 'DayCycleLightDark') {
    const phase = dayPhase(now);
    return phase === 'night'
      ? 'theme-cycle-dark-night'
      : `theme-cycle-light-${phase}`;
  }
  if (theme === 'DayCycleYear') {
    // The season's colours by day or night, a tint for the part of the day, and the decorations:
    // the holiday's, else the season's.
    const phase = dayPhase(now);
    const scene = yearScene(now, hidden);
    return [
      `theme-year-${scene.season}-${phase === 'night' ? 'night' : 'day'}`,
      `year-phase-${phase}`,
      `year-decor-${scene.holiday ?? scene.season}`,
    ].join(' ');
  }
  return `theme-${theme.toLowerCase()}`;
}

/** The seasonal themes, which bring their own decorations. */
const OWN_DECORATIONS = ['Halloween', 'Winter', 'Christmas', 'Spring', 'Autumn', 'Summer', 'Easter', 'NewYear', 'Eid', 'LunarNewYear', 'Diwali', 'Fastelavn', 'SanktHans', 'DayCycleYear'];

/**
 * The decorations a theme without its own gets when they are switched on: the season's figures,
 * or the holiday's — by the calendar, as on the "Følger året" theme. Null for the themes that bring
 * their own (they are switched off with the host's `no-decor` class instead).
 */
export function calendarDecorClass(theme: string, now: Date, hidden: readonly Holiday[] = []): string | null {
  if (OWN_DECORATIONS.includes(theme)) return null;
  const scene = yearScene(now, hidden);
  return `year-decor-${scene.holiday ?? scene.season}`;
}

/** Whether a theme brings its own decorations. */
export function hasOwnDecorations(theme: string): boolean {
  return OWN_DECORATIONS.includes(theme);
}

/**
 * The sky over a day-cycle theme: the sun crosses the top of the screen in an arc from sunrise to
 * sunset — up on the left, highest in the middle, down on the right — and the moon follows the
 * same arc from sunset to the next sunrise.
 */
export interface SkyBody {
  body: 'sun' | 'moon';
  /** How far across: 0 rising on the left, 1 setting on the right. */
  across: number;
  /** How high in the arc: 0 at the horizon (rising or setting), 1 at the top in the middle. */
  lift: number;
}

export function skyBody(now: Date): SkyBody {
  const { rise, set } = sunTimes(now);
  const minutes = minutesOf(now);
  const sun = minutes >= rise && minutes < set;
  const since = sun ? minutes - rise : (minutes - set + DAY) % DAY;
  const span = sun ? set - rise : DAY - (set - rise);
  const across = since / span;
  return {
    body: sun ? 'sun' : 'moon',
    across,
    lift: 4 * across * (1 - across),
  };
}
